import { useEffect, useState } from 'react'
import { subscribeToAudit } from '@/api/ws'
import { auditsApi } from '@/api/client'
import type { AuditPhase, AuditProgressMessage, ReportDetail } from '@/api/client'

interface AuditProgressState {
  phase: AuditPhase | null
  progress: number
  message: string
  result: ReportDetail | null
}

export function useAuditProgress(auditId: string | undefined) {
  const [state, setState] = useState<AuditProgressState>({
    phase: null,
    progress: 0,
    message: '',
    result: null,
  })

  useEffect(() => {
    if (!auditId) return
    let cancelled = false

    const fetchReport = async (message: string) => {
      try {
        const report = await auditsApi.getReport(auditId)
        if (cancelled) return
        setState({ phase: 'complete', progress: 100, message, result: report })
      } catch {
        if (cancelled) return
        setState({ phase: 'error', progress: 0, message: 'Could not load report', result: null })
      }
    }

    // Reload recovery: if we land on /audit/:id after the audit already finished,
    // there will be no WS broadcast — fetch the report directly.
    auditsApi.getReport(auditId).then(report => {
      if (cancelled) return
      if (report.completedAt) {
        setState({ phase: 'complete', progress: 100, message: 'Audit complete', result: report })
      }
    }).catch(() => { /* report not ready yet — wait for WS */ })

    const unsub = subscribeToAudit(auditId, (msg: AuditProgressMessage) => {
      if (cancelled) return
      if (msg.phase === 'complete') {
        // Show a transitional state while the full report is fetched. Keeping
        // phase !== 'complete' here ensures the AuditPage progress block stays
        // mounted instead of flashing blank between WS event and REST response.
        setState({ phase: 'scoring', progress: 100, message: 'Loading report…', result: null })
        fetchReport(msg.message || 'Audit complete')
      } else {
        setState({
          phase: msg.phase,
          progress: msg.progress,
          message: msg.message,
          result: null,
        })
      }
    })

    return () => {
      cancelled = true
      unsub()
    }
  }, [auditId])

  return state
}
